/**
 * Presentation Metadata Service
 *
 * Stores unified metadata (artifacts, citations, etc.) at the presentation level
 * using CustomXmlParts. Also maintains a local cache for fast startup hydration.
 */

import { getOrCreatedocumentId } from './documentIdManager';
import type { PresentationMetadataRegistry } from './metadataTypes';

const METADATA_NAMESPACE = 'http://schemas.slideknowledgegraph.com/carouselmetadata';
const LOCAL_CACHE_PREFIX = 'carousel_presentation_metadata_';

function escapeXml(str: string): string {
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
}

function metadataToXml(metadata: PresentationMetadataRegistry): string {
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<CarouselMetadata xmlns="${METADATA_NAMESPACE}">
  <Version>${metadata.version}</Version>
  <documentId>${escapeXml(metadata.documentId)}</documentId>
  <LastUpdated>${metadata.lastUpdated}</LastUpdated>
  <Entries>${escapeXml(JSON.stringify(metadata.entriesById))}</Entries>
  <ShapeIndex>${escapeXml(JSON.stringify(metadata.shapeIndex))}</ShapeIndex>
</CarouselMetadata>`;
}

function xmlToMetadata(xml: string, fallbackdocumentId: string): PresentationMetadataRegistry | null {
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(xml, 'text/xml');
        const getElement = (tag: string): string | null => {
            const el = doc.getElementsByTagName(tag)[0];
            return el?.textContent || null;
        };

        const version = getElement('Version') || '1.0';
        const documentId = getElement('documentId') || fallbackdocumentId;
        const lastUpdated = getElement('LastUpdated') || new Date().toISOString();
        const entriesStr = getElement('Entries');
        const shapeIndexStr = getElement('ShapeIndex');

        const entriesById = entriesStr ? JSON.parse(entriesStr) : {};
        const shapeIndex = shapeIndexStr ? JSON.parse(shapeIndexStr) : {};

        return {
            version,
            documentId,
            lastUpdated,
            entriesById,
            shapeIndex,
        };
    } catch (error) {
        console.warn('[PresentationMetadata] Failed to parse XML:', error);
        return null;
    }
}

export function getMetadataCacheKey(documentId: string): string {
    return `${LOCAL_CACHE_PREFIX}${documentId}`;
}

export function loadCachedPresentationMetadata(documentId: string): PresentationMetadataRegistry | null {
    try {
        if (typeof localStorage === 'undefined') {
            return null;
        }
        const raw = localStorage.getItem(getMetadataCacheKey(documentId));
        if (!raw) return null;
        return JSON.parse(raw) as PresentationMetadataRegistry;
    } catch (error) {
        console.warn('[PresentationMetadata] Failed to load cached metadata:', error);
        return null;
    }
}

export function cachePresentationMetadata(metadata: PresentationMetadataRegistry): void {
    try {
        if (typeof localStorage === 'undefined') {
            return;
        }
        localStorage.setItem(getMetadataCacheKey(metadata.documentId), JSON.stringify(metadata));
    } catch (error) {
        console.warn('[PresentationMetadata] Failed to cache metadata:', error);
    }
}

export async function loadPresentationMetadataFromPptx(
    documentId?: string
): Promise<PresentationMetadataRegistry | null> {
    const resolveddocumentId = documentId ?? await getOrCreatedocumentId();
    return PowerPoint.run(async (context) => {
        try {
            const parts = context.presentation.customXmlParts.getByNamespace(METADATA_NAMESPACE);
            parts.load('items');
            await context.sync();

            if (parts.items.length === 0) {
                return null;
            }

            // Multiple metadata parts can exist if previous writers failed to fully clean up.
            // Parse all candidates and pick the newest/best-populated one.
            const xmlResults = parts.items.map((part) => part.getXml());
            await context.sync();

            let selectedMetadata: PresentationMetadataRegistry | null = null;
            let selectedStats: { lastUpdatedTs: number; shapeCount: number; entryCount: number } | null = null;

            for (const xmlResult of xmlResults) {
                const parsed = xmlToMetadata(xmlResult.value, resolveddocumentId);
                if (!parsed) {
                    continue;
                }
                const entryCount = Object.keys(parsed.entriesById || {}).length;
                const shapeCount = Object.keys(parsed.shapeIndex || {}).length;
                const parsedTs = Date.parse(parsed.lastUpdated || "");
                const lastUpdatedTs = Number.isFinite(parsedTs) ? parsedTs : 0;

                if (
                    !selectedStats ||
                    lastUpdatedTs > selectedStats.lastUpdatedTs ||
                    (lastUpdatedTs === selectedStats.lastUpdatedTs && shapeCount > selectedStats.shapeCount) ||
                    (
                        lastUpdatedTs === selectedStats.lastUpdatedTs &&
                        shapeCount === selectedStats.shapeCount &&
                        entryCount > selectedStats.entryCount
                    )
                ) {
                    selectedMetadata = parsed;
                    selectedStats = { lastUpdatedTs, shapeCount, entryCount };
                }
            }

            if (parts.items.length > 1) {
                console.log(
                    `[PresentationMetadata] Found ${parts.items.length} metadata parts; selected ` +
                    `${selectedStats?.entryCount ?? 0} entries / ${selectedStats?.shapeCount ?? 0} shapes`
                );
            }

            if (selectedMetadata) {
                cachePresentationMetadata(selectedMetadata);
            }
            return selectedMetadata;
        } catch (error) {
            console.warn('[PresentationMetadata] Failed to load from PPTX:', error);
            return null;
        }
    });
}

export async function savePresentationMetadataToPptx(
    metadata: PresentationMetadataRegistry
): Promise<boolean> {
    return PowerPoint.run(async (context) => {
        try {
            const parts = context.presentation.customXmlParts.getByNamespace(METADATA_NAMESPACE);
            parts.load('items');
            await context.sync();

            for (const part of parts.items) {
                part.delete();
            }
            await context.sync();

            const xml = metadataToXml(metadata);
            context.presentation.customXmlParts.add(xml);
            await context.sync();

            cachePresentationMetadata(metadata);
            return true;
        } catch (error) {
            console.warn('[PresentationMetadata] Failed to save to PPTX:', error);
            return false;
        }
    });
}

export function createEmptyPresentationMetadata(documentId: string): PresentationMetadataRegistry {
    return {
        version: '1.0',
        documentId,
        lastUpdated: new Date().toISOString(),
        entriesById: {},
        shapeIndex: {},
    };
}
